// 3rd party libs
import React, { useEffect, useState } from 'react';
import CircularProgress, { CircularProgressProps } from '@mui/material/CircularProgress';
import ParentIcon from '@mui/icons-material/AccountBalanceOutlined';
import AddIcon from '@mui/icons-material/AddRounded';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import cx from 'classnames';

// local imports
import { DateFormatter, useDimensions } from '../../../helpers';
import styles from './ChildOrgCard.module.scss';
import { ORGANIZATION, ROLE_DESCRIPTION, USER_NEW } from '../../../enums/TYPES';
import Avatar from '../../modules/Avatar';
import GigsCount from '../../modules/GigsCount';

const roundedPercentage = (part: number, total: number, decimalPlaces: number) => {
  const result = (part / total) * 100;
  return result.toFixed(decimalPlaces);
};

function CircularProgressWithLabel(props: CircularProgressProps & { value: number; total: number }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" color="inherit" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Typography variant="caption" component="div">{`${roundedPercentage(props.value, props.total, 1)}%`}</Typography>
      </Box>
    </Box>
  );
}

export type User = {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  avatar_url: string;
  last_online?: string;
  created_at?: string;
  need_help?: string;
  invitation_token: string;
  organization_name?: string;
  organization: ORGANIZATION;
  organizations: ORGANIZATION[];
  organization_users?: {
    username: string;
    role: ROLE_DESCRIPTION;
    organization: ORGANIZATION;
    id: string;
  }[];
};

type UserRole = {
  id: string;
  title: string;
  description: string;
};

type TChildOrgCard = {
  data: ORGANIZATION;
  fetchAll?: () => Promise<unknown>;
  onOrgSelect: (org: ORGANIZATION | null, mouseClicked?: boolean) => void;
  selectedOrg: ORGANIZATION | null;
  loading: boolean;
  viewMode?: boolean;
};

const ChildOrgCard: React.FC<TChildOrgCard> = ({ data, fetchAll, onOrgSelect, selectedOrg, loading, viewMode }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editData, setEditData] = useState(data);
  const { widthD } = useDimensions();

  useEffect(() => {
    setEditData(data);
  }, [data]);

  const handleDeleteClick = (e: any) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setShowDeleteModal(true);
  };

  const onEditModalClose = () => {
    setShowEditModal(false);
    onOrgSelect(null);
  };

  const onEditModalOpen = (e: any) => {
    if (loading) {
      return;
    }

    e.stopPropagation();
    setShowEditModal(true);
  };

  const onDeleteModalClose = () => {
    setShowDeleteModal(false);
    onOrgSelect(null);
  };

  const onSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    onOrgSelect(data, true);
  };

  const [showAssignOrgModal, setShowAssignOrgModal] = useState(false);
  const onAssignOrgClick = (e?: any) => {
    e?.stopPropagation();
    setShowAssignOrgModal(!showAssignOrgModal);
  };

  return (
    <>
      <div
        className={cx(styles.card, selectedOrg && (selectedOrg?.id === data.id ? styles.selectedOrg : styles.notSelectedOrg))}
        onClick={onSelect}
      >
        <div className={styles.title} style={{ width: selectedOrg ? '40%' : '20%' }}>
          <Avatar avatar={data?.image_url} name={data.name} />
          <Tooltip title={data.type === 'parent' ? `"${data.name}" is parent organisation` : data.name} placement="bottom-start">
            <div className={styles.name}>
              {data.type === 'parent' && <ParentIcon style={{ fill: '#6500aa' }} />}
              {data.name}
            </div>
          </Tooltip>
        </div>
        <div className={styles.location}>
          <Tooltip title={data.location}>
            <span className={styles.locationName}>{data.location}</span>
          </Tooltip>
        </div>
        <div className={styles.storage}>
          {widthD > 800 ? (
            <GigsCount usage={data.storage_usage} limit={data.storage_limit} />
          ) : (
            <Tooltip title={`${Number.parseFloat(`${data.storage_usage}`).toFixed(1)}gb/ ${data.storage_limit}gb`}>
              <div>
                <CircularProgressWithLabel value={data.storage_usage} total={data.storage_limit} />
              </div>
            </Tooltip>
          )}
        </div>

        {viewMode && (
          <div className={styles.actionBtns}>
            {widthD > 800 ? (
              <GigsCount usage={+data.hours_limit_used.toFixed(1)} limit={data.hours_limit} hours={true} />
            ) : (
              <Tooltip title={`${Number.parseFloat(`${data.hours_limit_used.toFixed(1)}`).toFixed(1)}gb/ ${data.hours_limit}gb`}>
                <div>
                  <CircularProgressWithLabel value={+data.hours_limit_used.toFixed(1)} total={data.hours_limit} />
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ChildOrgCard;
